import React, {useState} from 'react'
import HubspotV2PropertiesProvider from 'organization/Event/Services/Apps/HubspotV2/Config/HubspotV2PropertiesProvider'
import Page from 'organization/Event/Page'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import {
  useHubspotV2,
  useServices,
} from 'organization/Event/Services/ServicesProvider'
import {
  ATTENDEE_CHECKED_IN,
  ATTENDEE_CREATED,
  ATTENDEE_SIGNED_WAIVER,
  HubspotV2Group,
  HubspotV2Integration,
  HubspotV2PlatformTag,
  IMPORT_TAG,
} from 'organization/Event/Services/Apps/HubspotV2/index'
import GroupsConfig from 'organization/Event/Services/Apps/HubspotV2/Config/GroupsConfig'
import {Property} from 'organization/Event/Services/Apps/HubspotV2/Config/HubspotV2PropertiesProvider'
import withStyles from '@material-ui/core/styles/withStyles'
import Button from 'lib/ui/Button'
import {useOrganization} from 'organization/OrganizationProvider'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import PlatformTagInput from 'organization/Event/Services/Apps/HubspotV2/Config/PlatformTagInput'
import FieldInput from 'organization/Event/Services/Apps/HubspotV2/Config/FieldInput.tsx'
import styled from 'styled-components'
import InfoAlert from 'lib/ui/alerts/InfoAlert'
import Grid from '@material-ui/core/Grid'
import PropertySelector from 'organization/Event/Services/Apps/HubspotV2/Config/PropertySelector'
import ImportAttendeesButton from 'organization/Event/Services/Apps/HubspotV2/Config/ImportAttendeesButton'
import {useToggle} from 'lib/toggle'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'

export interface Data {
  tags: HubspotV2PlatformTag[]
  groups: HubspotV2Group[]
  login_property: {
    label: string
    name: string
    type: string
  }
  tags_property: {
    label: string
    name: string
    type: string
  }
  phone_property: {
    label: string
    name: string
    type: string
  }
}

export type TargetProperty = Pick<Property, 'name' | 'label' | 'type'>
export type ImportProperty = TargetProperty & {value: string | null}

export default function Config() {
  return (
    <HubspotV2PropertiesProvider>
      <Content />
    </HubspotV2PropertiesProvider>
  )
}

function Content() {
  const hubspotV2 = useHubspotV2()
  const {save, processing} = useSave()
  const [successMessage, setSuccessMessage] = useState<null | string>(null)
  const clearSuccessMessage = () => setSuccessMessage(null)

  const [loginProperty, setLoginProperty] = useState<TargetProperty | null>({
    name: hubspotV2.login_property_name,
    label: hubspotV2.login_property_label,
    type: hubspotV2.login_property_type || 'text',
  })

  const [tagsProperty, setTagsProperty] = useState<TargetProperty | null>({
    name: hubspotV2.tags_property_name,
    label: hubspotV2.tags_property_label,
    type: hubspotV2.tags_property_type || 'text',
  })

  const [phoneProperty, setPhoneProperty] = useState<TargetProperty | null>({
    name: hubspotV2.phone_property_name || 'phone',
    label: hubspotV2.phone_property_label || 'Phone Number',
    type: hubspotV2.phone_property_type || 'phonenumber',
  })

  const {flag: showingImportSuccess, toggle: toggleImportSuccess} = useToggle()
  const [importError, setImportError] = useState('')
  const clearImportError = () => setImportError('')
  const [groups, setGroups] = useState<HubspotV2Group[]>(hubspotV2.groups)
  const [tags, setTags] = useState<HubspotV2PlatformTag[]>(hubspotV2.tags)
  const [errors, setErrors] = useState<Array<string>>([])

  const getTag = (type: HubspotV2PlatformTag['type']) => {
    const target = tags.find((t) => t.type === type)
    if (!target) {
      throw new Error(`Unknown tag type: ${type}`)
    }

    return target
  }

  const filledGroups = groups.filter((g) => g.key && g.property_name)

  const handleSave = () => {
    let errorsCheck: Array<string> = []

    if (!loginProperty?.name) {
      errorsCheck.push('loginProperty')
    }

    if (!tagsProperty?.name) {
      errorsCheck.push('tagsProperty')
    }

    const importTag = tags.find((t) => t.type === 'import_tag')
    if (!importTag || !importTag.property_name || !importTag.property_value) {
      errorsCheck.push('importTag')
    }

    if (
      importTag?.property_name === tagsProperty?.name ||
      importTag?.property_name === loginProperty?.name ||
      importTag?.property_name === phoneProperty?.name ||
      filledGroups.some((g) => g.property_name === importTag?.property_name) ||
      tags.some(
        (t) =>
          t.type !== 'import_tag' &&
          t.property_name === importTag?.property_name,
      )
    ) {
      errorsCheck.push('duplicatedImportTag')
    }

    setErrors(errorsCheck)

    if (errorsCheck.length > 0) {
      return
    }

    const data: Data = {
      tags,
      groups: filledGroups,
      tags_property: {
        label: tagsProperty?.label || '',
        name: tagsProperty?.name || '',
        type: tagsProperty?.type || '',
      },
      phone_property: {
        label: phoneProperty?.label || '',
        name: phoneProperty?.name || '',
        type: phoneProperty?.type || '',
      },
      login_property: {
        label: loginProperty?.label || '',
        name: loginProperty?.name || '',
        type: loginProperty?.type || '',
      },
    }

    save(data)
  }

  const updateTag = (tag: HubspotV2PlatformTag) => {
    const updated = tags.map((t) => (t.id === tag.id ? tag : t))
    setTags(updated)
  }

  const importTagError = () => {
    if (errors.includes('importTag')) {
      return 'Import tag is required'
    }

    if (errors.includes('duplicatedImportTag')) {
      return 'Import tag property must be unique and not used in other fields'
    }
  }

  return (
    <Page>
      <StyledSuccessAlert onClose={clearSuccessMessage}>
        {successMessage}
      </StyledSuccessAlert>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item md={6}>
            <Typography variant="h4">Hubspot V2</Typography>
          </Grid>
          <StyledGrid item md={6}>
            <ImportAttendeesButton
              onSuccess={toggleImportSuccess}
              onError={setImportError}
            />
          </StyledGrid>
        </Grid>
      </Box>

      <ErrorAlert onClose={clearImportError}>{importError}</ErrorAlert>
      <InfoAlert showing={showingImportSuccess}>
        Import successfully started. Depending on the number of subscribers.
      </InfoAlert>

      <Box mb={2}>
        <Typography>Hub ID: {hubspotV2.portal_id}</Typography>
      </Box>

      <Box mb={2}>
        <Box mb={2}>
          <Typography variant="h5">
            Attendee Import or Delete via Tag
          </Typography>
        </Box>

        <PlatformTagInput
          value={getTag(IMPORT_TAG)}
          onChange={updateTag}
          errorText={importTagError()}
        />
        <em>
          If an attendee with the given email already exists, obv.io will update
          the other attributes.
        </em>
      </Box>

      <Box mb={2}>
        <Typography variant="h5">Attendee Tags Property</Typography>
        <em>
          Obvio can import one Contact Property from Hubspot V2 that is a
          multiple choice property as tags within Obvio. This is optional.
        </em>
      </Box>

      <FieldInput
        tagsProperty={tagsProperty}
        onChange={setTagsProperty}
        targetTypes={['select', 'checkbox', 'text']}
        errorText={
          errors.includes('tagsProperty') ? 'Tags Property is required' : ''
        }
      />

      <Box mb={2}>
        <Typography variant="h5">Login URL Property</Typography>
        <em>
          Select the Contact Property in Hubspot V2 to save your attendee's
          Login URLs.
        </em>
      </Box>
      <PropertySelector
        label={loginProperty?.label}
        value={loginProperty}
        targetTypes={['text']}
        onChange={setLoginProperty}
        errorText={
          errors.includes('loginProperty') ? 'Login Property is required' : ''
        }
      />

      <Box mb={2}>
        <Typography variant="h5">Attendee Phone Property</Typography>
        <em>
          Obvio can import one Contact Property from Hubspot V2 that is a
          multiple choice property as tags within Obvio. This is optional.
        </em>
      </Box>
      <FieldInput tagsProperty={phoneProperty} onChange={setPhoneProperty} />

      <Box mb={2}>
        <Typography variant="h5">Action tags</Typography>
        <em>
          Obvio can apply a specific contact property for the following 3
          actions that take place within Obvio. These action tags are optional.
        </em>
      </Box>
      <PlatformTagInput value={getTag(ATTENDEE_CREATED)} onChange={updateTag} />
      <PlatformTagInput
        value={getTag(ATTENDEE_CHECKED_IN)}
        onChange={updateTag}
      />
      <PlatformTagInput
        value={getTag(ATTENDEE_SIGNED_WAIVER)}
        onChange={updateTag}
      />

      <Box mb={2}>
        <Typography variant="h5">Attendee Groups Properties</Typography>
        <em>
          Obvio can import Contact Properties from Hubspot V2 as groups within
          Obvio. You can import multiple properties by adding multiple groups.
          This is optional.
        </em>
      </Box>
      <GroupsConfig onChange={setGroups} groups={groups} />

      <SaveButton
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        aria-label="save"
        onClick={handleSave}
        disabled={processing}
        fullWidth
      >
        Save
      </SaveButton>
    </Page>
  )
}

function useSave() {
  const {client} = useOrganization()
  const {event} = useEvent()

  const [processing, setProcessing] = useState<boolean>(false)
  const {update: updateIntegration} = useServices()
  const [error, setError] = useState<string | null>(null)

  const save = (data: Data) => {
    setProcessing(true)
    client
      .put<HubspotV2Integration>(
        api(`/events/${event.id}/integrations/hubspot_v2`),
        data,
      )
      .then(updateIntegration)
      .catch((e) => setError(e.message))
      .finally(() => {
        setProcessing(false)
      })
  }

  return {
    processing,
    error,
    save,
  }
}

const StyledGrid = withStyles({
  root: {
    display: 'flex',
    justifyContent: 'end',
  },
})(Grid)

const StyledSuccessAlert = styled(InfoAlert)`
  margin-bottom: ${(props) => props.theme.spacing[5]};
`

const SaveButton = styled(Button)`
    marginTop: ${(props) => props.theme.spacing[5]},
    marginBottom: ${(props) => props.theme.spacing[5]},
`
